// NewDiscussionDialog.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import { getUserDetails } from '../../../../../../../storageService';
import { createDiscussionTopic } from '../../../../../../../Utils/apiService';
import { invalidateDiscussionTopicsCache } from '../../../../Helper/DiscussionForumHelper';
import { CourseSf } from '../../../../../../../interfaces/Common';

interface NewDiscussionDialogProps {
    open: boolean;
    onClose: () => void;
    // The batch the "Post topic" button was opened from — used as the
    // dialog's default selection, not the batch it necessarily posts to.
    batchId: string;
    // Every batch the faculty teaches — the dialog picks Training first,
    // then narrows to that training's batches, so batch numbers (which
    // repeat across trainings, e.g. "Batch 1") never get confused for one
    // another.
    courses: CourseSf[];
    onSuccess?: () => void;
}

const NewDiscussionDialog: React.FC<NewDiscussionDialogProps> = ({
    open,
    onClose,
    batchId,
    courses,
    onSuccess
}) => {
    const [question, setQuestion] = useState('');
    const [selectedBatchId, setSelectedBatchId] = useState(batchId);
    // Training the dialog's own batch dropdown is narrowed to — resolved
    // from whichever course the dialog opened with.
    const [selectedCourseName, setSelectedCourseName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset to the batch (and its training) the dialog was opened from each
    // time it opens.
    useEffect(() => {
        if (!open) return;
        setSelectedBatchId(batchId);
        const openedCourse = courses.find((c) => c.ProgramPlanId === batchId);
        setSelectedCourseName(openedCourse?.CourseName || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, batchId]);

    // Distinct trainings across every batch the faculty teaches.
    const courseNames = useMemo(
        () => Array.from(new Set(courses.map((c) => c.CourseName).filter(Boolean))),
        [courses]
    );

    // The batch dropdown only offers batches for the chosen Training,
    // sorted ascending (numeric-aware) so "Batch 2" comes before "Batch 10".
    const coursesForSelectedCourseName = useMemo(
        () => courses
            .filter((c) => c.CourseName === selectedCourseName && c.ProgramPlanId)
            .sort((a, b) => a.TermName.localeCompare(b.TermName, undefined, { numeric: true, sensitivity: 'base' })),
        [courses, selectedCourseName]
    );

    // Switching Training resets the batch pick to that training's first
    // batch, so the batch dropdown never keeps a stale pick from a
    // different training.
    useEffect(() => {
        if (!selectedCourseName) return;
        if (!coursesForSelectedCourseName.some((c) => c.ProgramPlanId === selectedBatchId)) {
            setSelectedBatchId((coursesForSelectedCourseName[0]?.ProgramPlanId as string) || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coursesForSelectedCourseName]);

    // Get user details
    const profile = getUserDetails();
    const contactId = profile?.ContactId;

    const handleSave = async () => {
        if (!question.trim()) {
            setError('Question is required');
            return;
        }
        if (!selectedBatchId) {
            setError('Select a batch to post this topic to');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await createDiscussionTopic({
                contactId,
                batchId: selectedBatchId,
                question,
            });
            invalidateDiscussionTopicsCache(selectedBatchId);

            // Reset form
            setQuestion('');

            // Close dialog and trigger success callback
            onClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Error creating discussion:', err);
            setError('Failed to create discussion. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxWidth: '720px',
                    boxShadow: '0 18px 50px rgba(31, 65, 114, 0.24)',
                },
            }}
        >
            <DialogTitle sx={{ fontSize: '18px', fontWeight: 700, color: '#202B3A', px: 3, pt: 2.5, pb: 1 }}>
                Post topic
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 3, py: 1.5 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>
                        {error}
                    </Alert>
                )}

                <label style={{ fontSize: '13px', fontWeight: 600, color: '#536276', marginTop: 4 }}>Training</label>
                <TextField
                    select
                    variant="outlined"
                    fullWidth
                    value={selectedCourseName}
                    onChange={(e) => setSelectedCourseName(e.target.value)}
                    required
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#F5F7FB',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#B8C8D9' },
                            '&.Mui-focused fieldset': { borderColor: '#1B8BCB', borderWidth: 1 },
                        },
                    }}
                >
                    {courseNames.map((name) => (
                        <MenuItem key={name} value={name}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>

                <label style={{ fontSize: '13px', fontWeight: 600, color: '#536276', marginTop: 4 }}>Batch</label>
                <TextField
                    select
                    variant="outlined"
                    fullWidth
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                    disabled={!selectedCourseName}
                    required
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#F5F7FB',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#B8C8D9' },
                            '&.Mui-focused fieldset': { borderColor: '#1B8BCB', borderWidth: 1 },
                        },
                    }}
                >
                    {coursesForSelectedCourseName.map((course) => (
                        <MenuItem key={course.ProgramPlanId} value={course.ProgramPlanId as string}>
                            {course.TermName || course.CourseName}
                        </MenuItem>
                    ))}
                </TextField>

                <label style={{ fontSize: '13px', fontWeight: 600, color: '#536276', marginTop: 4 }}>Question</label>
                <TextField
                    variant="outlined"
                    placeholder="Ask a question for the batch to discuss"
                    fullWidth
                    multiline
                    minRows={4}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    required
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#F5F7FB',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#B8C8D9' },
                            '&.Mui-focused fieldset': { borderColor: '#1B8BCB', borderWidth: 1 },
                        },
                        '& textarea': { fontSize: '14px' },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pt: 1.5, pb: 2.5, gap: 1.5 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        backgroundColor: '#fff',
                        color: '#687991',
                        border: '1px solid #D8E0EA',
                        borderRadius: '8px',
                        minWidth: '110px',
                        height: '38px',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#B8C8D9' },
                    }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    sx={{
                        backgroundColor: '#1B8BCB !important',
                        color: '#FFFFFF !important',
                        borderRadius: '8px',
                        minWidth: '150px',
                        height: '38px',
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 5px 14px rgba(27, 139, 203, 0.24)',
                        '&:hover': { backgroundColor: '#147DB9 !important' },
                        '&.Mui-disabled': {
                            backgroundColor: '#B9DDF0 !important',
                            color: '#3D6F8B !important',
                            opacity: 1,
                        },
                    }}
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || !question.trim() || !selectedBatchId}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Save and finish'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewDiscussionDialog;
